import { Link } from "react-router-dom";
import PageBanner from "../../PageBanner";
import SocialMediaSidebar from "../../SocialMediaSidebar";
import SectionHeader from "../../SectionHeader";
import climateImage from "../../../assets/images/temp2.png"; // Replace with correct image path
import ScrollToTopButton from "../../ScrollToTopButton";

const ClimateMonitoring = () => {
  return (
    <>
      <div>
        <SocialMediaSidebar />
      </div>
      <PageBanner
        title="Tracking Climate with Temperature & Air Sensors"
        breadcrumb={[
          { label: "Home", href: "/" },
          // { label: "Resources" },
          { label: "Blogs" },
        ]}
      />
      <div className="max-w-6xl mx-auto p-4">
        <SectionHeader
          title="Tracking Climate with Temperature & Air Sensors @ GyanYantram"
          shareUrl="https://yourdomain.com/blog/climate-monitoring"
        />
      </div>
      <div>
        <ScrollToTopButton />
      </div>

      <div className="w-full px-4 sm:px-6 md:px-10 lg:px-28 py-10 bg-white text-gray-800">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex flex-col lg:flex-row gap-8 mb-10">
            <div className="lg:w-2/3">
              <h4 className="text-[#00D084] text-2xl font-semibold mb-3">
                Summary
              </h4>
              <p className="text-black mb-4 text-lg">
                Smart poles equipped with environmental sensors enable cities to monitor temperature, humidity, and air quality in real time. 
                GyanYantram empowers decision-makers with accurate, actionable climate data for sustainable urban living.
              </p>
              <p className="text-black text-lg">
                These insights support efficient public health planning, pollution control, and proactive measures during heatwaves or poor air days.
              </p>
            </div>
            <div className="lg:w-1/3">
              <img src={climateImage} alt="Climate Monitoring Sensors" className="rounded-lg" />
              <p className="text-sm text-gray-500 mt-2 italic">
                Real-time data on air and temperature via embedded sensors
              </p>
            </div>
          </div>

          <div className="mb-10">
            <h4 className="text-[#00D084] text-2xl font-semibold mb-3">
              Introduction
            </h4>
            <div className="grid md:grid-cols-3 gap-6">
              <div className="md:col-span-2">
                <p className="text-black mb-4 text-lg">
                  As urban populations grow, monitoring environmental conditions is vital. Traditional methods lack scale and speed.
                </p>
                <p className="text-black text-lg">
                  GyanYantram’s climate-monitoring solutions use smart pole sensors to gather localized, live climate data—transforming how cities plan, respond, and evolve.
                </p>
              </div>
              <div className="bg-gray-50 p-4 rounded-lg border-l-4 border-[#00D084]">
                <h5 className="font-bold text-lg mb-2">Did You Know?</h5>
                <p className="text-gray-700">
                  Real-time air quality data helps reduce respiratory hospital visits by up to 25% in highly polluted cities.
                </p>
              </div>
            </div>
          </div>

          <div className="mb-10">
            <h5 className="text-xl font-semibold mb-4 text-black">
              Key Features of Climate Monitoring Integration:
            </h5>
            <div className="grid md:grid-cols-2 gap-6">
              <div>
                <div className="bg-white p-4 rounded-lg shadow-sm mb-4 border-l-4 border-blue-500">
                  <h6 className="font-semibold text-lg mb-2">Temperature & Humidity Sensing</h6>
                  <p className="text-gray-700">
                    Track fluctuations across neighborhoods for heatwave and comfort index monitoring.
                  </p>
                </div>
                <div className="bg-white p-4 rounded-lg shadow-sm mb-4 border-l-4 border-green-500">
                  <h6 className="font-semibold text-lg mb-2">Air Quality Index (AQI)</h6>
                  <p className="text-gray-700">
                    Measure pollutants like PM2.5, CO2, NOx for public safety and environmental compliance.
                  </p>
                </div>
                <div className="bg-white p-4 rounded-lg shadow-sm mb-4 border-l-4 border-purple-500">
                  <h6 className="font-semibold text-lg mb-2">Live Environmental Dashboard</h6>
                  <p className="text-gray-700">
                    View real-time data across smart poles on an interactive map-based interface.
                  </p>
                </div>
              </div>
              <div>
                <div className="bg-white p-4 rounded-lg shadow-sm mb-4 border-l-4 border-red-500">
                  <h6 className="font-semibold text-lg mb-2">Data-Driven Alerts</h6>
                  <p className="text-gray-700">
                    Get notified of extreme conditions like pollution spikes or rapid temperature shifts.
                  </p>
                </div>
                <div className="bg-white p-4 rounded-lg shadow-sm mb-4 border-l-4 border-yellow-500">
                  <h6 className="font-semibold text-lg mb-2">Cloud-Based Storage</h6>
                  <p className="text-gray-700">
                    Store historical climate data for trend analysis, urban planning, and research.
                  </p>
                </div>
                <div className="bg-white p-4 rounded-lg shadow-sm mb-4 border-l-4 border-indigo-500">
                  <h6 className="font-semibold text-lg mb-2">Analytics & Visualization</h6>
                  <p className="text-gray-700">
                    Generate heatmaps, pollution trend graphs, and actionable reports using AI analytics.
                  </p>
                </div>
              </div>
            </div>
          </div>

          <div className="mb-10 bg-gray-50 p-6 rounded-lg">
            <h5 className="text-xl font-semibold mb-4 text-black text-center">
              Impact of Real-Time Climate Data
            </h5>
            <div className="grid md:grid-cols-3 gap-4">
              <div className="bg-white p-4 rounded-lg shadow text-center">
                <div className="text-3xl font-bold text-[#00D084] mb-2">25%</div>
                <p className="text-gray-700">Reduction in pollution-related illnesses</p>
              </div>
              <div className="bg-white p-4 rounded-lg shadow text-center">
                <div className="text-3xl font-bold text-[#00D084] mb-2">30%</div>
                <p className="text-gray-700">More efficient public cooling strategies</p>
              </div>
              <div className="bg-white p-4 rounded-lg shadow text-center">
                <div className="text-3xl font-bold text-[#00D084] mb-2">40%</div>
                <p className="text-gray-700">Improvement in air quality awareness</p>
              </div>
            </div>
          </div>

          <div className="mb-10">
            <h5 className="text-xl font-semibold mb-3 text-black">Conclusion</h5>
            <div className="flex flex-col md:flex-row gap-6">
              <div className="md:w-2/3">
                <p className="text-black text-lg">
                  GyanYantram’s smart climate monitoring empowers cities to understand and improve their environment.
                  With sensor-rich poles, live dashboards, and AI tools, administrators can make sustainable, informed decisions.
                </p>
              </div>
              <div className="md:w-1/3 bg-[#00D084] bg-opacity-10 p-4 rounded-lg">
                <h6 className="font-semibold text-lg mb-2">Looking Ahead</h6>
                <p className="text-gray-700">
                  Future enhancements may include pollen tracking, UV index monitoring, and weather-based citizen alerts.
                </p>
              </div>
            </div>
          </div>

          <div className="bg-gray-50 p-6 rounded-lg">
            <h5 className="text-xl font-semibold mb-3 text-black">
              GyanYantram’s Contribution
            </h5>
            <div className="grid md:grid-cols-2 gap-6">
              <div>
                <p className="text-black mb-4 text-lg">
                  GyanYantram delivers robust environmental sensing through smart infrastructure.
                  Real-time air and temperature data enables cities to respond to climate challenges and promote healthier living.
                </p>
                <p className="text-black text-lg">
                  Learn more at{" "}
                  <a
                    href="https://gyanyantram.com"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-[#00D084] hover:underline"
                  >
                    www.gyanyantram.com
                  </a>
                </p>
              </div>
              <div>
                <ul className="list-disc pl-6 space-y-2 text-lg text-black">
                  <li><strong>Temperature Tracking:</strong> Monitor urban heat in real-time</li>
                  <li><strong>Air Quality Alerts:</strong> Detect and respond to pollution surges</li>
                  <li><strong>Cloud & AI Analytics:</strong> Forecast trends, visualize hotspots</li>
                  <li><strong>Smart City Ready:</strong> Ideal for green zones, schools, and roads</li>
                </ul>
              </div>
            </div>
          </div>
        </div>
      </div>
    </>
  );
};

export default ClimateMonitoring;
